import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const services = [
  {
    id: 'isnetworld',
    title: 'ISNetworld® Compliance',
    description:
      'Full-service account setup, ongoing maintenance, score improvement, and RAVS Plus management. We handle every requirement so your grades stay high.',
    features: ['New account setup', 'Score & grade improvement', 'RAVS Plus support', 'Ongoing maintenance'],
    accent: 'gold',
  },
  {
    id: 'veriforce',
    title: 'Veriforce Management',
    description:
      'Complete Veriforce compliance management from initial setup to continuous maintenance — keeping your Veriforce profile current and fully compliant.',
    features: ['Account creation', 'Document management', 'Compliance updates', 'Score optimization'],
    accent: 'dark',
  },
  {
    id: 'avetta',
    title: 'Avetta Qualification',
    description:
      'Expert Avetta account setup and qualification management. We navigate the complex prequalification process to get you approved and keep you working.',
    features: ['Qualification setup', 'Document submission', 'Ongoing compliance', 'Approval management'],
    accent: 'light',
  },
  {
    id: 'safety',
    title: 'Safety Training',
    description:
      'Onsite, online, classroom, career tech, tailgate, and consultant-led programs. We find and implement the best training solution for your workforce.',
    features: ['Onsite training', 'Online options', 'Classroom programs', 'Record management'],
    accent: 'dark',
  },
  {
    id: 'injury',
    title: 'Injury Rate Reduction',
    description:
      'Specialized TRIR, DART, and EMR reduction programs that protect your company from disqualification and save money on Workers\' Compensation insurance.',
    features: ['TRIR reduction', 'DART improvement', 'EMR optimization', 'WC savings'],
    accent: 'light',
  },
  {
    id: 'ravs',
    title: 'RAVS Plus Support',
    description:
      'Dedicated support for one of the most demanding ISNetworld requirements. We handle RAVS Plus documentation so you meet even the toughest operator standards.',
    features: ['RAVS preparation', 'Gap analysis', 'Submission management', 'Ongoing RAVS maintenance'],
    accent: 'gold',
  },
];

function ServiceCard({
  svc,
  idx,
}: {
  svc: (typeof services)[0];
  idx: number;
}) {
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>();
  const bg =
    svc.accent === 'gold'
      ? 'bg-[#C9A030]'
      : svc.accent === 'dark'
      ? 'bg-[#0A0A0A]'
      : 'bg-[#F5F0E8]';
  const titleColor =
    svc.accent === 'gold' ? 'text-[#0A0A0A]' : svc.accent === 'dark' ? 'text-[#F2EDE3]' : 'text-[#1A1409]';
  const bodyColor =
    svc.accent === 'gold' ? 'text-[#1A1409]/75' : svc.accent === 'dark' ? 'text-[#9A9A9A]' : 'text-[#5A5A5A]';
  const checkColor =
    svc.accent === 'gold' ? 'text-[#0A0A0A]' : 'text-[#C9A030]';
  const featureColor =
    svc.accent === 'gold' ? 'text-[#1A1409]/65' : svc.accent === 'dark' ? 'text-[#F2EDE3]/50' : 'text-[#1A1409]/55';
  const linkColor =
    svc.accent === 'gold' ? 'text-[#0A0A0A]' : 'text-[#C9A030]';

  return (
    <div
      ref={ref}
      className={`fade-up ${isVisible ? 'visible' : ''} ${bg} p-8 lg:p-10 flex flex-col`}
      style={{ transitionDelay: `${idx * 0.07}s` }}
    >
      <div className={`w-8 h-0.5 mb-6 ${svc.accent === 'gold' ? 'bg-[#0A0A0A]' : 'bg-[#C9A030]'}`} />
      <h3 className={`font-serif text-xl font-medium mb-3 leading-snug ${titleColor}`}>{svc.title}</h3>
      <p className={`text-sm font-light leading-relaxed mb-6 flex-1 ${bodyColor}`}>{svc.description}</p>
      <ul className="space-y-2 mb-7">
        {svc.features.map((f) => (
          <li key={f} className="flex items-center gap-2.5">
            <span className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0">
              <i className={`ri-check-line text-xs ${checkColor}`} />
            </span>
            <span className={`text-xs font-light ${featureColor}`}>{f}</span>
          </li>
        ))}
      </ul>
      <Link
        to="/services"
        className={`inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-semibold cursor-pointer transition-opacity hover:opacity-70 whitespace-nowrap ${linkColor}`}
      >
        Learn More
        <span className="w-3.5 h-3.5 flex items-center justify-center">
          <i className="ri-arrow-right-line text-xs" />
        </span>
      </Link>
    </div>
  );
}

export default function ServicesOverview() {
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>();

  return (
    <section className="bg-[#F5F0E8] py-24 px-6 lg:px-14">
      <div className="max-w-2xl mb-14">
        <p className="overline mb-4">What We Do</p>
        <h2 className="font-serif text-4xl lg:text-5xl font-light text-[#1A1409] leading-tight gold-line">
          Compliance Solutions That Protect Your Contracts
        </h2>
        <p className="text-[#5A5A5A] text-base font-light mt-8 leading-relaxed max-w-xl">
          We have the experience to set up, maintain, or improve your account scores and grades across all major contractor prequalification platforms.
        </p>
      </div>

      <div
        ref={ref}
        className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1A1409]/08 fade-up ${isVisible ? 'visible' : ''}`}
      >
        {services.map((svc, idx) => (
          <ServiceCard key={svc.id} svc={svc} idx={idx} />
        ))}
      </div>

      <div className="mt-12 flex flex-wrap items-center justify-between gap-6">
        <p className="text-[#5A5A5A] text-sm font-light">
          Not sure which service you need? We&rsquo;ll assess your situation at no obligation.
        </p>
        <Link
          to="/contact"
          className="inline-flex items-center gap-3 bg-[#1A1409] text-[#F2EDE3] px-8 py-4 text-[11px] font-bold tracking-[0.22em] uppercase whitespace-nowrap cursor-pointer transition-colors duration-200 hover:bg-[#C9A030] hover:text-[#0A0A0A]"
        >
          Start Your Compliance Journey
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-line text-xs" />
          </span>
        </Link>
      </div>
    </section>
  );
}
